import { useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

import { CarouselItem } from '@jane/bloom/components/communicationBanner/carouselBanner/carouselItem/carouselItem';
import { ImagePagination } from '@jane/bloom/components/imagePagination/imagePagination';
import type { CarouselBanner } from '@jane/bloom/types/schemas/roots/carouselBanner';

import styles from './carouselSwiper.module.css';

interface CarouselSwiperProps {
  activeIndex: number;
  items: CarouselBanner;
  storeName?: string;
  updateIndex: (newIndex: number) => void;
  variant?: 'brand' | 'product';
}

export const CarouselSwiper = ({
  activeIndex,
  items,
  storeName,
  updateIndex,
}: CarouselSwiperProps) => {
  const totalItems = items.length;

  const nextIndex = (activeIndex + 1) % totalItems;
  const prevIndex = (activeIndex + totalItems - 1) % totalItems;

  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      updateIndex(nextIndex);
    },
    onSwipedRight: () => {
      updateIndex(prevIndex);
    },
  });

  const arrowKeyHandlers = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      updateIndex(prevIndex);
    }

    if (event.key === 'ArrowRight') {
      updateIndex(nextIndex);
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const onCarouselItemClick = (index: number) => () => {
    updateIndex(index);
  };

  return (
    <div className={styles['carouselWrapper']}>
      <div
        className={styles['carouselTrack']}
        {...touchHandlers}
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {items.map((item, index) => (
          <CarouselItem
            key={index}
            activeIndex={activeIndex}
            imageSrc={item.image_url}
            imageAlt={item.alt_text ?? `${storeName} Marketing Banner`}
            onClick={onCarouselItemClick(index)}
            variant="product"
            url={item.url}
            index={index}
          />
        ))}
      </div>
      {totalItems > 1 && (
        <ImagePagination
          activeIndex={activeIndex}
          totalItems={totalItems}
          updateIndex={updateIndex}
        />
      )}
    </div>
  );
};
